import React from 'react';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import { Box, Button, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import caloriesByMealHeaderIcon from '@/assets/images/calories-by-meal-header-icon.svg';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';

/** Figma node 26:10900 — meal calorie bar track width (px). Section frame: 26:12739. */
const CALORIES_BY_MEAL_BAR_TRACK_PX = 207;

/** Shared with meal-plan board `buildDayAnalysis` and manual nutrition page. */
export type AnalysisMealRow = {
  key: string;
  label: string;
  calories: number;
  percent: number;
  color: string;
};

export type NutrientVsRdaBar = {
  key: string;
  label: string;
  percent: number;
  color: string;
};

export type ContributorRow = {
  key: string;
  title: string;
  detailLabel: string;
  mealLabel: string;
  mealColor: string;
  calories: number;
  percent: number;
  color: string;
};

export type FatBreakdownRow = {
  key: string;
  label: string;
  value: number;
  percent: number;
  color: string;
};

const FAT_RING_COLORS: Record<string, string> = {
  mono: '#20aedc',
  trans: '#b7edff',
  poly: '#e0e0e0',
  saturated: '#2ec3b6',
};

const FAT_BREAKDOWN_RENDER_ORDER = ['mono', 'trans', 'poly', 'saturated'];

export type DayAnalysisSnapshot = {
  dayLabel: string;
  totalCalories: number;
  totalMeals: number;
  macroSplit: { carbs: number; protein: number; fat: number };
  nutrientVsRda: NutrientVsRdaBar[];
  mealCalories: AnalysisMealRow[];
  macroProgressCards: Array<{ key: string; label: string; value: number; target: number; color: string }>;
  totalFat: number;
  fatTarget: number;
  fatBreakdown: FatBreakdownRow[];
  topContributors: ContributorRow[];
};

function getProgressValue(value: number, target: number): number {
  if (!target || target <= 0) return 0;
  return Math.max(0, (value / target) * 100);
}

function getCirclePercentFontSize(percent: number): string {
  const label = `${roundNutritionDisplay(percent)}%`;
  if (label.length >= 8) return '12px';
  if (label.length >= 7) return '12px';
  if (label.length >= 6) return '12px';
  return '12px';
}

function MacroRingCard({
  label,
  value,
  target,
  color,
}: {
  label: string;
  value: number;
  target: number;
  color: string;
}) {
  const percent = getProgressValue(value, target);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #f3f4f6',
        bgcolor: '#fff',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
        px: '22px',
        py: '30px',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', px: '43px', width: '100%' }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: 700,
            color,
            textTransform: 'uppercase',
            letterSpacing: '1.0938px',
            lineHeight: '14.25px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>

        <Box sx={{ position: 'relative', width: 66, height: 66 }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={66}
            thickness={5.5}
            sx={{ color: '#dbe6ee', position: 'absolute', inset: 0 }}
          />
          <CircularProgress
            variant="determinate"
            value={Math.min(100, percent)}
            size={66}
            thickness={5.5}
            sx={{
              color,
              position: 'absolute',
              inset: 0,
              '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
            }}
          />
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: getCirclePercentFontSize(percent),
                fontWeight: 700,
                color: '#1e2939',
                letterSpacing: '-0.04px',
                lineHeight: '16px',
              }}
            >
              {roundNutritionDisplay(percent)}%
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#1e2939', lineHeight: '20px', letterSpacing: '-0.0762px', whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ fontSize: '16px', lineHeight: '19.5px' }}>
            {roundNutritionDisplay(value)}g{' '}
          </Box>
          <Box component="span" sx={{ fontSize: '12px', fontWeight: 400, color: '#99a1af', letterSpacing: '0.1172px', lineHeight: '15px' }}>
            / {roundNutritionDisplay(target)}g RDA
          </Box>
        </Typography>
      </Box>
    </Paper>
  );
}

export interface ManualMealPlanAnalysisDashboardProps {
  selectedAnalysis: DayAnalysisSnapshot;
  /** Shown in the Calories by Meal header (e.g. client EER). */
  targetDailyCalories: number;
  onClose: () => void;
  onViewDetailedAnalysis?: () => void;
  /** Matches Figma subheading, e.g. "Week 1 · 6 meals · Detailed Breakdown". */
  weekLabel?: string;
}

export function ManualMealPlanAnalysisDashboard({
  selectedAnalysis,
  targetDailyCalories,
  onClose,
  onViewDetailedAnalysis,
  weekLabel = 'Week 1',
}: ManualMealPlanAnalysisDashboardProps) {
  const orderedFatBreakdown = React.useMemo(() => {
    return FAT_BREAKDOWN_RENDER_ORDER
      .map((key) => selectedAnalysis.fatBreakdown.find((item) => item.key === key))
      .filter((item): item is FatBreakdownRow => Boolean(item));
  }, [selectedAnalysis.fatBreakdown]);

  const fatRingSegments = React.useMemo(() => {
    const total = orderedFatBreakdown.reduce((sum, item) => sum + Math.max(0, item.percent), 0);
    if (total <= 0 || orderedFatBreakdown.length === 0) return [];

    const strokeW = 9;
    const r = 47.5 - strokeW / 2;
    const circumference = 2 * Math.PI * r;
    const gap = orderedFatBreakdown.length > 1 ? strokeW + 2 : 0;
    let dashOffset = 0;

    return orderedFatBreakdown.map((item) => {
      const arcLen = (Math.max(0, item.percent) / total) * circumference;
      const visualArcLen = Math.max(0.001, arcLen - gap);
      const segment = {
        key: item.key,
        color: FAT_RING_COLORS[item.key] ?? item.color,
        strokeDasharray: `${visualArcLen} ${circumference - visualArcLen}`,
        strokeDashoffset: -dashOffset,
      };
      dashOffset += arcLen;
      return {
        ...segment,
      };
    });
  }, [orderedFatBreakdown]);

  const nutrientVsGoalsAxisMax = React.useMemo(() => {
    const dataMax = selectedAnalysis.nutrientVsRda.reduce((max, n) => Math.max(max, n.percent), 0);
    const rawAxisMax = Math.max(100, dataMax);
    return Math.ceil(rawAxisMax / 25) * 25;
  }, [selectedAnalysis.nutrientVsRda]);

  const analysisTitle = selectedAnalysis.dayLabel.trim() || 'Analysis';
  const subtitleLine = `${weekLabel} · ${selectedAnalysis.totalMeals} meals · Detailed Breakdown`;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Figma node 26:11269 — gradient header */}
      <Box
        sx={{
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(167deg, #0d82a5 0%, #1ea9d6 100%)',
          px: 2.5,
          pt: 2.5,
          pb: 2.25,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -32,
            right: -32,
            width: 128,
            height: 128,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 32,
            right: 40,
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: 0.75,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
            <IconButton
              onClick={onClose}
              aria-label="Close analysis"
              sx={{
                width: 24,
                height: 24,
                p: 0,
                color: '#fff',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                flexShrink: 0,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.28)' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>

          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, width: '100%' }}>
              <Box sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: 220, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '17px',
                    fontWeight: 700,
                    lineHeight: '25.5px',
                    letterSpacing: '-0.43px',
                    color: '#fff',
                  }}
                >
                  {analysisTitle}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '16.5px',
                    letterSpacing: '0.06px',
                    color: '#fff',
                  }}
                >
                  {subtitleLine}
                </Typography>
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  textAlign: 'right',
                  minWidth: 95,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '37.5px',
                    fontWeight: 800,
                    lineHeight: '37.5px',
                    letterSpacing: '0.31px',
                    color: '#fff',
                  }}
                >
                  {roundNutritionDisplay(selectedAnalysis.totalCalories)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14.422px',
                    fontWeight: 400,
                    lineHeight: '21.633px',
                    letterSpacing: '0.17px',
                    color: '#fff',
                  }}
                >
                  kcal total
                </Typography>
              </Box>
            </Box>

  
          </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
          overflowX: 'auto',
          p: 2,
          pt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: '#f3f4f6',
        }}
      >
        {onViewDetailedAnalysis ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button
              type="button"
              onClick={onViewDetailedAnalysis}
              variant="outlined"
              aria-label="View detailed report"
              sx={{
                minWidth: 0,
                height: 34,
                px: 1.75,
                borderRadius: '8px',
                borderColor: '#20AEDC',
                bgcolor: '#fff',
                color: '#0b8fbd',
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                fontWeight: 700,
                lineHeight: 1,
                boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
                '&:hover': {
                  borderColor: '#0b8fbd',
                  bgcolor: '#eefaff',
                },
              }}
            >
              View Detailed Report
            </Button>
          </Box>
        ) : null}

        {/* Figma node 26:10826 — Nutrient vs RDA */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 2,
            borderRadius: '16px',
            border: '1px solid #f3f4f6',
            bgcolor: '#fff',
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
            p: 2.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              height: 21,
              minHeight: 21,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '10px',
                  bgcolor: '#fffbeb',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <BarChartRoundedIcon sx={{ fontSize: 12, color: '#f59e0b' }} />
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13px',
                  fontWeight: 700,
                  lineHeight: '19.5px',
                  letterSpacing: '-0.0762px',
                  color: '#1e2939',
                }}
              >
                Intake vs Goals
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', columnGap: 0.5, height: 200, flexShrink: 0 }}>
            <Box sx={{ position: 'relative', height: '100%' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 24,
                }}
              >
                {[1, 0.75, 0.5, 0.25, 0].map((fraction) => (
                  <Typography
                    key={`nutrient-y-${fraction}`}
                    sx={{
                      position: 'absolute',
                      top: `${(1 - fraction) * 100}%`,
                      right: 0,
                      transform: 'translateY(-50%)',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400,
                      color: '#9ca3af',
                      lineHeight: 'normal',
                      textAlign: 'right',
                    }}
                  >
                    {`${roundNutritionDisplay(nutrientVsGoalsAxisMax * fraction)}%`}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Box sx={{ position: 'relative', height: '100%', minHeight: 0 }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 24,
                }}
              >
                {[0, 25, 50, 75].map((value) => (
                  <Box
                    key={`guide-${value}`}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: `${value}%`,
                      borderTop: '1px dashed #d6dfe8',
                    }}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 24,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
                  alignItems: 'end',
                  columnGap: '7.36px',
                }}
              >
                {selectedAnalysis.nutrientVsRda.map((nutrient) => (
                  <Tooltip key={nutrient.key} title={`${roundNutritionDisplay(nutrient.percent)}%`} arrow placement="top">
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        height: '100%',
                        width: '100%',
                        minWidth: 0,
                        cursor: 'pointer',
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height:
                            nutrient.percent > 0
                              ? `${Math.max((5 / nutrientVsGoalsAxisMax) * 100, (nutrient.percent / nutrientVsGoalsAxisMax) * 100)}%`
                              : 0,
                          maxHeight: '100%',
                          bgcolor: nutrient.color,
                          borderRadius: '4px 4px 0 0',
                        }}
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
                  alignItems: 'flex-start',
                  columnGap: '7.36px',
                  pt: 0.25,
                }}
              >
                {selectedAnalysis.nutrientVsRda.map((nutrient) => (
                  <Typography
                    key={`axis-${nutrient.key}`}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400,
                      color: '#9ca3af',
                      textAlign: 'center',
                      lineHeight: 'normal',
                    }}
                  >
                    {nutrient.label}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Figma node 26:12739 — Calories by Meal (list + bars; bar track 26:10900) */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '16px',
            border: '1px solid #f3f4f6',
            bgcolor: '#fff',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1)',
            overflow: 'visible',
            p: '1px',
          }}
        >
          <Box
            sx={{
              px: 2,
              minHeight: 43,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #f9fafb',
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '10px',
                  bgcolor: '#fff7ed',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={caloriesByMealHeaderIcon}
                  alt=""
                  sx={{ width: 12, height: 12, display: 'block' }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#1e2939',
                  letterSpacing: '-0.0762px',
                  lineHeight: '19.5px',
                }}
              >
                Calories by Meal
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                fontWeight: 400,
                color: '#99a1af',
                letterSpacing: '0.1172px',
                lineHeight: '15px',
              }}
            >
              Target: ~{roundNutritionDisplay(targetDailyCalories)} kcal
            </Typography>
          </Box>

          <Stack spacing={1} sx={{ px: 2, pt: 1.5, pb: 1.5, minWidth: 0 }}>
            {(selectedAnalysis.mealCalories ?? []).length === 0 ? (
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#99a1af',
                  lineHeight: 1.5,
                  py: 1,
                  textAlign: 'center',
                }}
              >
                No meals on this day yet.
              </Typography>
            ) : (
              (selectedAnalysis.mealCalories ?? []).map((meal) => {
                const totalDay = Math.max(1, selectedAnalysis.totalCalories);
                const fillPct = Math.min(100, (meal.calories / totalDay) * 100);

                return (
                  <Box
                    key={meal.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      minHeight: 22,
                      minWidth: 0,
                      width: '100%',
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meal.color, flexShrink: 0 }} />

                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '17.25px',
                        color: '#364153',
                        letterSpacing: '0.0337px',
                        flex: '0 0 88px',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {meal.label}
                    </Typography>

                    <Box
                      sx={{
                        flex: '1 1 auto',
                        minWidth: 48,
                        maxWidth: CALORIES_BY_MEAL_BAR_TRACK_PX,
                        height: 6,
                        borderRadius: '12px',
                        bgcolor: '#f3f4f6',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${fillPct}%`,
                          height: '100%',
                          bgcolor: meal.color,
                          borderRadius: '12px',
                          minWidth: meal.calories > 0 ? 2 : 0,
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        fontWeight: 600,
                        lineHeight: '16.5px',
                        color: '#364153',
                        textAlign: 'right',
                        letterSpacing: '0.0645px',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        minWidth: '4.5rem',
                      }}
                    >
                      {roundNutritionDisplay(meal.calories)} kcal
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '15px',
                        color: '#99a1af',
                        textAlign: 'right',
                        letterSpacing: '0.1172px',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        width: 36,
                      }}
                    >
                      {roundNutritionDisplay(meal.percent)}%
                    </Typography>
                  </Box>
                );
              })
            )}
          </Stack>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.25 }}>
          {selectedAnalysis.macroProgressCards.map((card) => (
            <MacroRingCard
              key={card.key}
              label={card.label}
              value={card.value}
              target={card.target}
              color={card.color}
            />
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: '16px',
            border: '1px solid #f3f4f6',
            bgcolor: '#fff',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
            p: '15px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'nowrap',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                flexShrink: 0,
                width: '30%',
              }}
            >
                <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: selectedAnalysis.macroProgressCards.find(c => c.key === 'total-fat' || c.key === 'fat')?.color ?? '#20aedc',
                  textTransform: 'uppercase',
                  letterSpacing: '1.0938px',
                  lineHeight: '14.25px',
                }}
              >
                Total Fat
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: 95,
                    height: 95,
                    position: 'relative',
                  }}
                >
                  <svg width="95" height="95" viewBox="0 0 95 95" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0 }}>
                    <circle cx="47.5" cy="47.5" r="43" fill="none" stroke="#f3f4f6" strokeWidth="9" />
                    {fatRingSegments.map((segment) => (
                      <circle
                        key={segment.key}
                        cx="47.5"
                        cy="47.5"
                        r="43"
                        fill="none"
                        stroke={segment.color}
                        strokeWidth="9"
                        strokeLinecap="round"
                        strokeDasharray={segment.strokeDasharray}
                        strokeDashoffset={segment.strokeDashoffset}
                      />
                    ))}
                  </svg>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 8,
                      borderRadius: '50%',
                      bgcolor: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: getCirclePercentFontSize(getProgressValue(selectedAnalysis.totalFat, selectedAnalysis.fatTarget)),
                        color: '#1e2939',
                        letterSpacing: '-0.0762px',
                        lineHeight: '19.5px',
                      }}
                    >
                      {roundNutritionDisplay(getProgressValue(selectedAnalysis.totalFat, selectedAnalysis.fatTarget))}%
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    color: '#1e2939',
                    lineHeight: 0,
                    letterSpacing: '-0.0762px',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                  }}
                >
                  <Box component="span" sx={{ fontSize: '16px', lineHeight: '19.5px' }}>
                    {roundNutritionDisplay(selectedAnalysis.totalFat)}g{' '}
                  </Box>
                  <Box component="span" sx={{ fontSize: '12px', fontWeight: 400, color: '#99a1af', letterSpacing: '0.1172px', lineHeight: '15px' }}>
                    / {roundNutritionDisplay(selectedAnalysis.fatTarget)}g RDA
                  </Box>
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', minWidth: 0, width: '70%' }}>
              {orderedFatBreakdown.map((item) => (
                  <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '999px', bgcolor: FAT_RING_COLORS[item.key] ?? item.color, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 400, color: '#374151', lineHeight: '18px', whiteSpace: 'nowrap' }}>
                      {item.label}{' '}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {roundNutritionDisplay(item.value)}g
                      </Box>
                      <Box component="span" sx={{ color: '#9ca3af' }}>
                        ({roundNutritionDisplay(item.percent)}%)
                      </Box>
                    </Typography>
                  </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: '16px',
            border: '1px solid #f3f4f6',
            bgcolor: '#fff',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <StarOutlineRoundedIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 700, color: '#1e2939' }}>
                Top Calorie Contributors
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#99a1af', fontWeight: 400 }}>
              {selectedAnalysis.topContributors.length} food items total
            </Typography>
          </Box>

          <Stack spacing={0} sx={{ mt: 0.95 }}>
            {selectedAnalysis.topContributors.map((item, index) => (
              <Box
                key={item.key}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '20px minmax(0, 1fr) auto',
                  gap: 0.72,
                  alignItems: 'center',
                  py: 0.82,
                  borderTop: index === 0 ? 'none' : '1px solid #edf2f7',
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: '#f2f4f7',
                    display: 'grid',
                    placeItems: 'center',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.75rem',
                    color: '#667085',
                    fontWeight: 600,
                  }}
                >
                  {index + 1}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#344054', fontWeight: 700, lineHeight: 1.2 }}>
                    {item.title}
                  </Typography>
                  <Box sx={{ mt: 0.18, display: 'flex', alignItems: 'center', gap: 0.28, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3', fontWeight: 500 }}>
                      {item.detailLabel}
                    </Typography>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: item.mealColor }} />
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: item.mealColor, fontWeight: 500 }}>
                      {item.mealLabel}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#25354d', fontWeight: 700 }}>
                    {roundNutritionDisplay(item.calories)} kcal
                  </Typography>
                  <Box sx={{ mt: 0.24, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.45 }}>
                    <Box sx={{ width: 46, height: 3, borderRadius: 99, bgcolor: '#edf2f7', overflow: 'hidden' }}>
                      <Box sx={{ width: `${Math.max(4, Math.min(100, item.percent))}%`, height: '100%', borderRadius: 99, bgcolor: item.color }} />
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#9aa8b8', fontWeight: 500 }}>
                      {roundNutritionDisplay(item.percent)}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
